import type { EncounterService, Period } from '@quromedical/fhir-common';
import {
  type AdmissionGetResponse,
  MeasurementName,
  type VitalsLatestResult,
  type VitalsLiveDataMeasures,
  type WebsocketAlert,
} from '@quromedical/models';
import { OxygenSaturationScale } from '@quromedical/news';
import { compact, maxby } from '@quromedical/utils';
import {
  colors,
  dashboardPrimaryMeasureNames,
  dashboardSecondaryMeasureNames,
  getECGConfig,
  getEcgTags,
  getMeasureProps,
  getNEWS2StatusBarProps,
  getNoteDisplay,
  getPatchBatteryProps,
  getPrimaryMeasurePropMapper,
  getServiceProps,
  icons,
  titles,
} from 'core/vitals';
import { VitalSummaryCard } from 'design-system/components';
import { router } from 'expo-router';
import type React from 'react';
import { useCallback } from 'react';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import { strings } from 'strings';

export interface DashboardCardProps {
  admission?: AdmissionGetResponse;
  liveData?: VitalsLiveDataMeasures;
  title?: string;
  serviceType?: EncounterService;
  servicePeriod?: Period;
  patientId?: string;
  oxygenSaturationScale?: OxygenSaturationScale;
  canViewIssues?: boolean;
  canViewAlerts?: boolean;
  alerts?: WebsocketAlert[];
}

const getSecondaryMeasurePropMapper =
  (latest: Partial<Record<MeasurementName, VitalsLatestResult>>) =>
  (name: MeasurementName) => {
    const props = getMeasureProps(latest, name);
    return {
      ...props,
      // remove colors from secondary measures to keep things a bit cleaner
      color: undefined,
    };
  };

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  patientId,
  serviceType,
  oxygenSaturationScale = OxygenSaturationScale.Scale1,
  // default liveData so that we can still display the card even without data for a patient
  liveData = {
    ecg: [],
    latest: {},
  },
  alerts = [],
  canViewAlerts = false,
  servicePeriod,
}) => {
  const { latest, ecg } = liveData;

  const ecgData = ecg.map((reading) => ({ x: reading.ts, y: reading.value }));

  const graphSummary = getMeasureProps(latest, MeasurementName.HeartRate, true);

  const primaryMeasures = dashboardPrimaryMeasureNames.map(
    getPrimaryMeasurePropMapper(latest, oxygenSaturationScale),
  );

  const secondaryMeasures = dashboardSecondaryMeasureNames.map(
    getSecondaryMeasurePropMapper(latest),
  );

  const patchProps = getPatchBatteryProps(latest);
  const serviceProps = getServiceProps(serviceType, servicePeriod);

  const metrics = compact([patchProps, serviceProps]);

  const graphProps = getECGConfig({
    aspectRatio: 6 / 1,
    baseData: ecg,
  });

  const latestEcg = maxby(ecg, (d) => d.ts);
  const ecgNote = getNoteDisplay(latestEcg?.ts);

  const onCardPress = useCallback(() => {
    if (!patientId) {
      return;
    }

    router.push({
      pathname: `/patient/${patientId}/profile`,
    });
  }, [patientId]);

  const statusBarProps = getNEWS2StatusBarProps(latest, oxygenSaturationScale);

  const ecgTags = canViewAlerts ? getEcgTags(alerts) : undefined;

  return (
    <TouchableWithoutFeedback onPress={onCardPress}>
      <VitalSummaryCard
        header={{
          title,
          icon: 'person',
          metrics,
        }}
        statusBar={{
          ...statusBarProps,
          title: strings.EWSTitle,
        }}
        graph={{
          title: titles[MeasurementName.ECG],
          icon: icons[MeasurementName.ECG],
          color: colors[MeasurementName.ECG],
          note: ecgNote,
          aspectRatio: graphProps.aspectRatio,
          data: ecgData,
          xDomain: graphProps.xDomain,
          yDomain: graphProps.yDomain,
          xSubTicks: graphProps.xSubTicks,
          ySubTicks: graphProps.ySubTicks,
          xTicks: graphProps.xTicks,
          yTicks: graphProps.yTicks,
          summary: {
            ...graphSummary,
            tags: ecgTags,
          },
        }}
        primaryMeasures={primaryMeasures}
        secondaryMeasures={secondaryMeasures}
      />
    </TouchableWithoutFeedback>
  );
};
